<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetMeterGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const meterTypes = ref(null);
  const meterGroups = ref(null);
  const units = ref(['n/a', 'kWh', 'MWh', 'm3', 'GJ']);
  const ux = ref([]);
  const b_1548630417156826_dataSource = computed(() => ({
    name: 'suggest_meter',
  }));
  const b_7072234632991584_parameters = computed(() => ({
    type: 'property',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    meterTypes: meterTypes.value,
    meterGroups: meterGroups.value,
    units: units.value,
    ux: ux.value,
    b_1548630417156826_dataSource: b_1548630417156826_dataSource.value,
    b_7072234632991584_parameters: b_7072234632991584_parameters.value,
  }));

  const $$load_meterTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('MeterTypes');
  };
  const $$load_meterGroups = async () => {
    return $fn.value
      .fetch('list_group')
      .then((data) => data.data.map((p) => p.name));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'meterTypes' },
      { name: 'meterGroups' },
      { name: 'units' },
      { name: 'ux' },
      { name: 'b_1548630417156826_dataSource', nodeId: '1548630417156826' },
      { name: 'b_7072234632991584_parameters', nodeId: '7072234632991584' },
    ],
    loaders: { meterTypes: $$load_meterTypes, meterGroups: $$load_meterGroups },
    ux: {},
    dataRefs: {
      labelAlign,
      meterTypes,
      meterGroups,
      units,
      ux,
      b_1548630417156826_dataSource,
      b_7072234632991584_parameters,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_7315092382398562_reloadComponentData_trbf25e3v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_usrm67q4a =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_trbf25e3v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_usrm67q4a],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_usrm67q4a,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_usrm67q4a = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_usrm67q4a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend class="text-muted">
      {{ pgettext('Meter', 'General') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="meter_type"
      :label="pgettext('Meter', 'Meter type')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormSelect2
        name="meter_type"
        v-model="meter.meter_type"
        :value="meter.meter_type"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.meter_type =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="meterTypes"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      name="parent"
      :label="pgettext('Meter', 'Main meter')"
      :label-cols-md="4"
      :content-cols-md="8"
      :labelAlign="labelAlign"
    >
      <ItpSelect2
        name="parent"
        v-model="meter.parent"
        :value="meter.parent"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.parent =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        clearable
        :dataSource="b_1548630417156826_dataSource"
        :initialOptions="meter.main_meter ? [meter.main_meter] : []"
        valueField="id"
        textField="serial_number"
        use-option-template-for-selected-option
        optionTemplate='<UcSuggestMeterOption :option="option"></UcSuggestMeterOption>'
      >
      </ItpSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="meter_unit"
      :label="pgettext('Meter', 'Unit and multiplier')"
      :labelAlign="labelAlign"
      contentClass="row"
      required
      :helpTooltip="
        pgettext(
          'Meter',
          'The multiplier converts the readings into the given units.'
        )
      "
    >
      <div class="col-4">
        <ItpFormSelect2
          name="unit"
          v-model="meter.unit"
          :value="meter.unit"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (meter.unit =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="units"
          :placeholder="pgettext('Meter', 'Unit')"
        >
        </ItpFormSelect2>
      </div>
      <div class="col-4">
        <ItpFormInput
          type="number"
          name="multiplier"
          v-model="meter.multiplier"
          :value="meter.multiplier"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (meter.multiplier =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :placeholder="pgettext('Meter', 'Multiplier')"
          :title="pgettext('Meter', 'Multiplier')"
        >
        </ItpFormInput>
      </div>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="model_name"
      :label="pgettext('Meter', 'Model name')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="model_name"
        v-model="meter.model_name"
        :value="meter.model_name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.model_name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="smart_device"
      :label="pgettext('Meter', 'Smart Device')"
      :labelAlign="labelAlign"
    >
      <ItpFormCheckbox
        name="smart_device"
        v-model="meter.smart_device"
        :value="meter.smart_device"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.smart_device =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormCheckbox>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="groups"
      :label="pgettext('Meter', 'Groups')"
      :labelAlign="labelAlign"
    >
      <ItpFormSelect2
        name="groups"
        v-model="meter.groups"
        :value="meter.groups"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.groups =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        multiple
        :options="meterGroups"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Meter', 'Site')"
      :labelAlign="labelAlign"
    >
      <UcSiteSelector
        :item="meter"
        path="site"
        :parameters="b_7072234632991584_parameters"
      >
      </UcSiteSelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Meter', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="description"
        v-model="meter.description"
        :value="meter.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <hr />
    <legend class="text-muted">
      {{ pgettext('Meter', 'Numbers') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="serial_number"
      :label="pgettext('Meter', 'Serial number')"
      :labelAlign="labelAlign"
      :helpTooltip="pgettext('Meter', 'The device\'s unique serial number.')"
      required
    >
      <ItpFormInput
        name="serial_number"
        v-model="meter.serial_number"
        :value="meter.serial_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.serial_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="deveui"
      :label="pgettext('Meter', 'DevEUI')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'Meter',
          '64-bit globally-unique Extended Unique Identifier (EUI-64) assigned by the manufacturer, or the owner, of the end-device.'
        )
      "
    >
      <ItpFormInput
        name="deveui"
        v-model="meter.deveui"
        :value="meter.deveui"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.deveui =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="tag_id"
      :label="pgettext('Meter', 'Tag ID')"
      :labelAlign="labelAlign"
      :helpTooltip="
        pgettext(
          'Meter',
          'Locally-unique tag ID to be used within the company.'
        )
      "
    >
      <ItpFormInput
        name="tag_id"
        v-model="meter.tag_id"
        :value="meter.tag_id"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (meter.tag_id =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <hr />
    <FieldsetMeterValidityData :meter="meter" :labelAlign="labelAlign">
    </FieldsetMeterValidityData>
  </ItpUsercontrol>
</template>
